import { useEffect, useRef, useState, useCallback } from "react";
import { hasChanged } from "../utils/configChecksum/configComparer";
import { debounce } from "../commonFunctions/arrayFunc";

const deepClone = (obj) => {
  if (typeof structuredClone === "function") return structuredClone(obj);
  return JSON.parse(JSON.stringify(obj));
};

const useDirtyChecker = (currentConfig, debounceMs = 300) => {
  const [isDirty, setIsDirty] = useState(false);
  const initialRef = useRef(null);
  const didInitializeRef = useRef(false);
  const debouncedCheckRef = useRef(null);

  const runCheck = useCallback(() => {
    if (!initialRef.current) return;
    const dirty = hasChanged(initialRef.current, currentConfig);
    setIsDirty(dirty);
  }, [currentConfig]);

  useEffect(() => {
    debouncedCheckRef.current = debounce(runCheck, debounceMs);
    return () => {
      debouncedCheckRef.current?.cancel?.();
    };
  }, [runCheck, debounceMs]);

  useEffect(() => {
    if (!didInitializeRef.current && currentConfig) {
      initialRef.current = deepClone(currentConfig);
      didInitializeRef.current = true;
      return;
    }
    if (!initialRef.current || !debouncedCheckRef.current) return;
    debouncedCheckRef.current();
  }, [currentConfig]);

  const markClean = useCallback(() => {
    initialRef.current = deepClone(currentConfig);
    didInitializeRef.current = true;
    setIsDirty(false);
  }, [currentConfig]);

  return { isDirty, markClean };
};

export default useDirtyChecker;
